<script setup lang="ts">
import type { FormInstance, FormItemRule } from 'element-plus'
import type {
  SchemaFormBuiltinRender,
  SchemaFormExpose,
  SchemaFormFooterScope,
  SchemaFormItem,
  SchemaFormItemScope,
  SchemaFormModel,
  SchemaFormOption,
  SchemaFormOptions,
  SchemaFormRenderFnContext,
} from './types'
import { FaIcon, FaInput, FaNumberField, FaSlider, FaSwitch, FaTextarea, FaTooltip } from '@fantastic-admin/components'
import { useWindowSize } from '@vueuse/core'
import {
  ElAutocomplete,
  ElCascader,
  ElCol,
  ElColorPicker,
  ElForm,
  ElFormItem,
  ElInput,
  ElMention,
  ElRate,
  ElRow,
  ElSelect,
  ElSelectV2,
  ElSpace,
  ElTimeSelect,
  ElTransfer,
  ElTreeSelect,
  ElUpload,
} from 'element-plus'
import { h, isProxy, isVNode, markRaw, nextTick, toRaw, useAttrs, useSlots } from 'vue'
import AppDatePicker from '@/components/AppForm/DatePicker.vue'
import AppTimePicker from '@/components/AppForm/TimePicker.vue'
import { buildSchemaFieldOptionChildren, resolveSchemaFieldBuiltinComponent, resolveSchemaFieldSharedExtraProps } from '@/components/schemaFieldRegistry'
import { useDictionaryStore } from '@/store/modules/dictionary'
import SubFormArray from './SubFormArray.vue'

type RuntimeSchemaFormItem = SchemaFormItem<any>

defineOptions({
  name: 'FaSchemaForm',
  inheritAttrs: false,
})

const props = withDefaults(defineProps<FaSchemaFormProps>(), {
  items: () => [],
  options: () => ({}),
  loading: undefined,
  disabled: undefined,
})

defineSlots<FaSchemaFormSlots>()

/**
 * `FaSchemaForm` 运行时 props。
 * 这里的注释主要服务于跳到实现文件时的悬停提示。
 */
interface FaSchemaFormProps {
  /**
   * 表单字段 schema。
   */
  items?: RuntimeSchemaFormItem[]
  /**
   * 表单整体配置。
   */
  options?: SchemaFormOptions
  /**
   * 强制覆盖 loading 状态。
   * 优先级高于 `options.loading`。
   */
  loading?: boolean
  /**
   * 强制覆盖 disabled 状态。
   * 优先级高于 `options.disabled`。
   */
  disabled?: boolean
}

/**
 * `FaSchemaForm` 具名插槽定义。
 */
interface FaSchemaFormSlots {
  /**
   * 完整接管表单内容。
   */
  default?: () => any
  /**
   * 表单头部说明区。
   */
  before?: () => any
  /**
   * 表单底部操作区。
   */
  footer?: (props: { formRef: FormInstance | undefined, model: Record<string, any>, loading: boolean }) => any
  /**
   * 自定义 loading 区域。
   */
  loading?: (props: { loading: boolean }) => any
  [key: string]: ((props?: any) => any) | undefined
}

const model = defineModel<SchemaFormModel>({
  default: () => ({}),
})

const attrs = useAttrs()
const slots = useSlots()
const formRef = ref<FormInstance>()
const innerItems = shallowRef<RuntimeSchemaFormItem[]>([])
const innerOptions = ref<SchemaFormOptions>({})
const optionMap = ref<Record<string, SchemaFormOption[]>>({})
const componentRefMap: Record<string, any> = {}
const formItemRefMap: Record<string, any> = {}
const dictionaryStore = useDictionaryStore()
const initialModelSnapshot = shallowRef<Record<string, any>>({})
const { width } = useWindowSize()

const builtinComponentMap: Record<string, any> = {
  autocomplete: ElAutocomplete,
  cascader: ElCascader,
  colorPicker: ElColorPicker,
  datePicker: AppDatePicker,
  input: FaInput,
  inputNumber: FaNumberField,
  mention: ElMention,
  number: FaNumberField,
  rate: ElRate,
  select: ElSelect,
  selectV2: ElSelectV2,
  slider: FaSlider,
  switch: FaSwitch,
  textarea: FaTextarea,
  timePicker: AppTimePicker,
  timeSelect: ElTimeSelect,
  transfer: ElTransfer,
  treeSelect: ElTreeSelect,
  upload: ElUpload,
}

const localBuiltinComponentMap: Record<string, any> = {
  FaInput,
  FaNumberField,
  FaSlider,
  FaSwitch,
  FaTextarea,
}

watch(
  () => props.items,
  (items) => {
    innerItems.value = normalizeItems(items)
  },
  { immediate: true },
)

watch(
  () => props.options,
  (options) => {
    innerOptions.value = { ...options }
  },
  { immediate: true },
)

watch(
  model,
  (value) => {
    if (!Object.keys(initialModelSnapshot.value).length) {
      initialModelSnapshot.value = structuredClone(toRaw(value))
    }
  },
  { immediate: true, deep: true },
)

const resolvedOptions = computed<SchemaFormOptions>(() => ({
  labelWidth: innerOptions.value.labelWidth ?? 96,
  cols: innerOptions.value.cols ?? 2,
  containerClass: innerOptions.value.containerClass,
  layout: innerOptions.value.layout ?? 'flex',
  flex: {
    gutter: innerOptions.value.flex?.gutter ?? 16,
    justify: innerOptions.value.flex?.justify,
    align: innerOptions.value.flex?.align ?? 'top',
    tag: innerOptions.value.flex?.tag,
  },
  grid: {
    direction: innerOptions.value.grid?.direction ?? 'vertical',
    size: innerOptions.value.grid?.size ?? 'default',
    wrap: innerOptions.value.grid?.wrap ?? true,
    fill: innerOptions.value.grid?.fill ?? true,
    fillRatio: innerOptions.value.grid?.fillRatio,
    alignment: innerOptions.value.grid?.alignment,
    class: innerOptions.value.grid?.class,
    prefixCls: innerOptions.value.grid?.prefixCls,
    style: innerOptions.value.grid?.style ?? 'width: 100%',
    spacer: innerOptions.value.grid?.spacer,
  },
  loading: props.loading ?? innerOptions.value.loading ?? false,
  loadingConfig: {
    text: innerOptions.value.loadingConfig?.text ?? '加载中...',
    spinner: innerOptions.value.loadingConfig?.spinner,
    svg: innerOptions.value.loadingConfig?.svg,
    viewBox: innerOptions.value.loadingConfig?.viewBox ?? '-9, -9, 50, 50',
    background: innerOptions.value.loadingConfig?.background,
    customClass: innerOptions.value.loadingConfig?.customClass,
    lock: innerOptions.value.loadingConfig?.lock ?? true,
    fullscreen: innerOptions.value.loadingConfig?.fullscreen ?? false,
  },
  disabled: props.disabled ?? innerOptions.value.disabled ?? false,
  readonly: false,
  size: innerOptions.value.size,
  rules: innerOptions.value.rules,
  inline: innerOptions.value.inline,
  labelPosition: innerOptions.value.labelPosition,
  labelSuffix: innerOptions.value.labelSuffix,
  hideRequiredAsterisk: innerOptions.value.hideRequiredAsterisk,
  requireAsteriskPosition: innerOptions.value.requireAsteriskPosition,
  showMessage: innerOptions.value.showMessage,
  inlineMessage: innerOptions.value.inlineMessage,
  statusIcon: innerOptions.value.statusIcon,
  validateOnRuleChange: innerOptions.value.validateOnRuleChange,
  scrollToError: innerOptions.value.scrollToError,
  scrollIntoViewOptions: innerOptions.value.scrollIntoViewOptions,
  onValidate: innerOptions.value.onValidate,
  beforeSlot: innerOptions.value.beforeSlot,
  footerSlot: innerOptions.value.footerSlot,
  afterSlot: innerOptions.value.afterSlot,
}))

const formBindProps = computed(() => {
  const {
    containerClass,
    layout,
    flex,
    grid,
    cols,
    loading,
    loadingConfig,
    beforeSlot,
    footerSlot,
    afterSlot,
    ...formOptions
  } = resolvedOptions.value

  return {
    ...attrs,
    ...formOptions,
    model: model.value,
  }
})

const isMobile = computed(() => width.value < 768)
const hasDefaultSlot = computed(() => Boolean(slots.default))

function resolveItemProp(item: RuntimeSchemaFormItem) {
  if (typeof item.prop === 'function') {
    return item.prop()
  }
  return item.prop == null ? undefined : String(item.prop)
}

function getValueByPath(target: Record<string, any>, path?: string) {
  if (!path) {
    return undefined
  }
  return String(path).split('.').reduce<any>((current, key) => current?.[key], target)
}

function setValueByPath(target: Record<string, any>, path: string, value: any) {
  const pathSegments = String(path).split('.')
  const lastKey = pathSegments.pop()
  if (!lastKey) {
    return
  }

  let current: Record<string, any> = target
  pathSegments.forEach((segment) => {
    if (!current[segment] || typeof current[segment] !== 'object') {
      current[segment] = {}
    }
    current = current[segment]
  })
  current[lastKey] = value
}

function normalizeRenderable<T>(render: SchemaFormItem<T>['render']) {
  if (!render || typeof render === 'string' || typeof render === 'function' || isVNode(render)) {
    return render
  }
  return markRaw(isProxy(render) ? toRaw(render) : render)
}

function normalizeItem(item: RuntimeSchemaFormItem): RuntimeSchemaFormItem {
  const normalizedItem: RuntimeSchemaFormItem = {
    ...item,
    render: normalizeRenderable(item.render),
    children: item.children?.map(child => normalizeItem(child as RuntimeSchemaFormItem)),
  }

  return normalizedItem
}

function normalizeItems(items: RuntimeSchemaFormItem[] = []): RuntimeSchemaFormItem[] {
  return items.map(item => normalizeItem(item))
}

function isShown(item: RuntimeSchemaFormItem) {
  if (item.dependencies?.length) {
    item.dependencies.forEach(dep => getValueByPath(model.value, dep))
  }
  if (typeof item.show === 'function') {
    return item.show(item, model.value)
  }
  if (item.show != null) {
    return item.show
  }
  return true
}

function isHiddenByHide(item: RuntimeSchemaFormItem) {
  if (typeof item.hide === 'function') {
    return item.hide(item, model.value)
  }
  return Boolean(item.hide)
}

function resolveLabel(item: RuntimeSchemaFormItem) {
  return typeof item.label === 'function' ? item.label() : item.label
}

function resolveLabelTooltipContent(item: RuntimeSchemaFormItem, scope: SchemaFormItemScope) {
  if (typeof item.labelTooltip === 'function') {
    return item.labelTooltip(scope)
  }
  return item.labelTooltip
}

function isRenderFactory(render: SchemaFormItem['render']): render is (context: SchemaFormRenderFnContext) => any {
  return typeof render === 'function' && !('setup' in render) && !('render' in render)
}

function normalizeBuiltinRender(render?: SchemaFormItem['render']): SchemaFormBuiltinRender | undefined {
  if (typeof render !== 'string') {
    return undefined
  }
  return render as SchemaFormBuiltinRender
}

function resolveBuiltinComponent(item: RuntimeSchemaFormItem) {
  const renderType = normalizeBuiltinRender(item.render) ?? 'input'
  if (renderType === 'subForm') {
    return SubFormArray
  }
  return resolveSchemaFieldBuiltinComponent(
    renderType,
    resolveOptions(item, createScope(item)),
    name => localBuiltinComponentMap[name] ?? builtinComponentMap[name] ?? ElInput,
    {
      cascader: ElCascader,
      select: ElSelect,
      slider: FaSlider,
    },
    ElInput,
  )
}

function resolveRenderProps(item: RuntimeSchemaFormItem, scope: SchemaFormItemScope) {
  if (typeof item.renderProps === 'function') {
    return item.renderProps(scope)
  }
  return item.renderProps ?? {}
}

function normalizeDictValue(value: string, type: SchemaFormItem['dictValueType']) {
  if (type === 'number') {
    return Number(value)
  }
  if (type === 'boolean') {
    return ['1', 'true', 'yes', 'on'].includes(value.toLowerCase())
  }
  return value
}

function inferDictValueType(item: RuntimeSchemaFormItem) {
  if (item.dictValueType) {
    return item.dictValueType
  }
  const currentValue = getValueByPath(model.value, resolveItemProp(item))
  if (typeof currentValue === 'number') {
    return 'number'
  }
  if (typeof currentValue === 'boolean') {
    return 'boolean'
  }
  return 'string'
}

function resolveDeclaredOptions(item: RuntimeSchemaFormItem, scope: SchemaFormItemScope): SchemaFormOption[] {
  if (typeof item.options === 'function') {
    return item.options(scope)
  }
  return item.options ?? []
}

function resolveOptions(item: RuntimeSchemaFormItem, scope: SchemaFormItemScope): SchemaFormOption[] {
  const key = resolveItemProp(item)
  if (key && optionMap.value[key]) {
    return optionMap.value[key]
  }
  return resolveDeclaredOptions(item, scope)
}

function normalizeRules(rules: FormItemRule[] | undefined) {
  return rules ? [...rules] : []
}

function resolveItemRules(item: RuntimeSchemaFormItem, scope: SchemaFormItemScope) {
  const prop = resolveItemProp(item)
  const globalRules = normalizeRules(prop ? resolvedOptions.value.rules?.[prop] as FormItemRule[] | undefined : undefined)
  const dynamicRules = typeof item.rules === 'function' ? item.rules(scope) : item.rules
  const itemProps = typeof item.itemProps === 'function' ? item.itemProps(scope) : (item.itemProps ?? {})
  const itemRules = normalizeRules(itemProps.rules as FormItemRule[] | undefined)
  const rules = [...globalRules, ...normalizeRules(dynamicRules), ...itemRules]
  if (item.customValidator) {
    rules.push({ validator: item.customValidator, trigger: 'blur' })
  }
  if (item.asyncValidator) {
    rules.push({ asyncValidator: item.asyncValidator, trigger: 'blur' })
  }
  return rules.length ? rules : undefined
}

function resolveItemProps(item: RuntimeSchemaFormItem, scope: SchemaFormItemScope) {
  const itemProps = typeof item.itemProps === 'function' ? item.itemProps(scope) : (item.itemProps ?? {})
  return {
    ...itemProps,
    rules: resolveItemRules(item, scope),
  }
}

function resolveColProps(item: RuntimeSchemaFormItem) {
  const cols = item.cols ?? {}
  if (isMobile.value) {
    const xs = typeof cols.xs === 'number' ? { span: cols.xs } : (cols.xs ?? {})
    return { span: 24, ...xs }
  }
  return {
    span: cols.span,
    offset: cols.offset,
    push: cols.push,
    pull: cols.pull,
    order: cols.order,
    flex: cols.flex,
    tag: cols.tag,
    xs: cols.xs ?? 24,
    sm: cols.sm,
    md: cols.md ?? (resolvedOptions.value.cols === 1 ? 24 : 12),
    lg: cols.lg,
    xl: cols.xl,
  }
}

function getFieldValue(item: RuntimeSchemaFormItem) {
  return getValueByPath(model.value, resolveItemProp(item))
}

function setFieldValue(item: RuntimeSchemaFormItem, value: any) {
  const prop = resolveItemProp(item)
  if (!prop) {
    return
  }
  setValueByPath(model.value, prop, value)
}

function createRenderContext(item: RuntimeSchemaFormItem): SchemaFormRenderFnContext {
  return {
    item,
    model: model.value,
    formData: model.value,
    formRef: formRef.value,
    disabled: Boolean(resolvedOptions.value.disabled),
    readonly: Boolean((resolvedOptions.value as any).readonly),
    size: resolvedOptions.value.size,
  }
}

function createScope(item: RuntimeSchemaFormItem): SchemaFormItemScope {
  return {
    ...createRenderContext(item),
    prop: resolveItemProp(item) ?? '',
    value: getFieldValue(item),
    setValue: value => setFieldValue(item, value),
  }
}

function createFooterScope(): SchemaFormFooterScope {
  const scope = {
    model: model.value,
    formData: model.value,
    formRef: formRef.value,
    loading: Boolean(resolvedOptions.value.loading),
    disabled: Boolean(resolvedOptions.value.disabled),
    readonly: Boolean((resolvedOptions.value as any).readonly),
    size: resolvedOptions.value.size,
    items: innerItems.value as RuntimeSchemaFormItem[],
    isMobile: isMobile.value,
  }

  return scope as SchemaFormFooterScope
}

function traverseItems(items: RuntimeSchemaFormItem[], visitor: (item: RuntimeSchemaFormItem) => void) {
  items.forEach((item) => {
    visitor(item)
    if (item.children?.length) {
      traverseItems(item.children, visitor)
    }
  })
}

function findItemByProp(items: RuntimeSchemaFormItem[], prop: string): RuntimeSchemaFormItem | undefined {
  let matched: RuntimeSchemaFormItem | undefined
  traverseItems(items, (item) => {
    if (!matched && resolveItemProp(item) === prop) {
      matched = item
    }
  })
  return matched
}

function flattenItems(items: RuntimeSchemaFormItem[]) {
  const result: RuntimeSchemaFormItem[] = []
  traverseItems(items, item => result.push(item))
  return result
}

function removeItemRecursive(items: RuntimeSchemaFormItem[], prop: string): boolean {
  const index = items.findIndex(item => resolveItemProp(item) === prop)
  if (index >= 0) {
    items.splice(index, 1)
    delete optionMap.value[prop]
    delete componentRefMap[prop]
    delete formItemRefMap[prop]
    return true
  }
  return items.some(item => item.children?.length ? removeItemRecursive(item.children, prop) : false)
}

function updateItemRecursive(items: RuntimeSchemaFormItem[], prop: string, updates: Partial<RuntimeSchemaFormItem>): boolean {
  const item = findItemByProp(items, prop)
  if (!item) {
    return false
  }
  Object.assign(item, updates)
  return true
}

function resolveRenderSlots(item: RuntimeSchemaFormItem, scope: SchemaFormItemScope) {
  if (typeof item.renderSlots === 'function') {
    return item.renderSlots(scope)
  }
  return item.renderSlots ?? {}
}

function resolveDynamicSlot(name: string) {
  return slots[name]
}

function resolveItemSlot(item: RuntimeSchemaFormItem, scope: SchemaFormItemScope, name: string) {
  const configuredSlot = item.itemSlots?.[name]
  const dynamicSlot = resolveDynamicSlot(`${name}-${scope.prop}`)
  if (dynamicSlot) {
    return (...args: any[]) => dynamicSlot({ ...scope, ...args[0] })
  }
  if (configuredSlot) {
    return (...args: any[]) => configuredSlot({ ...scope, ...args[0] })
  }
  return undefined
}

function setComponentRef(prop: string, instance: any) {
  if (componentRefMap[prop] === instance) {
    return
  }
  componentRefMap[prop] = instance
}

function setFormItemRef(prop: string, instance: any) {
  if (formItemRefMap[prop] === instance) {
    return
  }
  formItemRefMap[prop] = instance
}

function buildOptionChildren(item: RuntimeSchemaFormItem, scope: SchemaFormItemScope) {
  const renderType = normalizeBuiltinRender(item.render)
  const options = resolveOptions(item, scope)
  return renderType ? buildSchemaFieldOptionChildren(renderType, scope.prop, options) : undefined
}

function resolveBuiltinExtraProps(item: RuntimeSchemaFormItem, scope: SchemaFormItemScope) {
  const renderType = normalizeBuiltinRender(item.render) ?? 'input'
  const options = resolveOptions(item, scope)

  if (renderType === 'subForm') {
    return {
      config: item.subForm ?? {
        items: [],
      },
    }
  }

  return resolveSchemaFieldSharedExtraProps(renderType, options)
}

function createBoundComponentVNode(component: any, item: RuntimeSchemaFormItem, scope: SchemaFormItemScope, extraProps: Record<string, any> = {}, extraSlots?: Record<string, any>) {
  const renderProps = resolveRenderProps(item, scope)
  const renderType = normalizeBuiltinRender(item.render) ?? 'input'
  const isFaSlider = renderType === 'slider' && component === FaSlider
  const isFaNumberField = component === FaNumberField
  const modelValue = isFaSlider
    ? [Number(scope.value ?? 0)]
    : isFaNumberField
      ? Number(scope.value ?? 0)
      : scope.value

  return h(component, {
    ...extraProps,
    ...renderProps,
    'modelValue': modelValue,
    'disabled': renderProps.disabled ?? resolvedOptions.value.disabled,
    'size': renderProps.size ?? resolvedOptions.value.size,
    'class': renderProps.class,
    'ref': scope.prop ? (instance: any) => setComponentRef(scope.prop, instance) : undefined,
    'onUpdate:modelValue': (value: any) => {
      setFieldValue(item, isFaSlider ? value?.[0] ?? 0 : value)
      renderProps['onUpdate:modelValue']?.(value)
    },
  }, extraSlots)
}

function renderFieldContent(item: RuntimeSchemaFormItem) {
  const scope = createScope(item)

  if (item.renderItem) {
    return item.renderItem(scope)
  }

  const render = item.render

  if (!render || typeof render === 'string') {
    const component = resolveBuiltinComponent(item)
    const renderSlots = resolveRenderSlots(item, scope)
    const optionSlot = buildOptionChildren(item, scope)
    const mergedSlots = optionSlot && !renderSlots.default
      ? { ...renderSlots, default: optionSlot }
      : renderSlots

    return createBoundComponentVNode(
      component,
      item,
      scope,
      resolveBuiltinExtraProps(item, scope),
      mergedSlots,
    )
  }

  if (isVNode(render)) {
    return render
  }

  if (isRenderFactory(render)) {
    const renderResult = render(createRenderContext(item))
    if (isVNode(renderResult)) {
      return renderResult
    }
    return createBoundComponentVNode(renderResult as any, item, scope, {}, resolveRenderSlots(item, scope))
  }

  return createBoundComponentVNode(render, item, scope, {}, resolveRenderSlots(item, scope))
}

function renderChildren(items: RuntimeSchemaFormItem[]) {
  if (!items.length) {
    return null
  }
  if (resolvedOptions.value.layout === 'grid') {
    return h(
      ElSpace as any,
      {
        direction: resolvedOptions.value.grid?.direction,
        size: resolvedOptions.value.grid?.size,
        wrap: resolvedOptions.value.grid?.wrap,
        fill: resolvedOptions.value.grid?.fill,
        fillRatio: resolvedOptions.value.grid?.fillRatio,
        alignment: resolvedOptions.value.grid?.alignment,
        class: ['mt-4 w-full', resolvedOptions.value.grid?.class],
        style: resolvedOptions.value.grid?.style,
        spacer: resolvedOptions.value.grid?.spacer,
      },
      () => items.map(child => renderSingleItem(child)),
    )
  }
  return h(
    ElRow,
    {
      gutter: resolvedOptions.value.flex?.gutter,
      class: 'mt-4 w-full',
    },
    () => items.map(child => renderSingleItem(child)),
  )
}

function renderSingleItem(item: RuntimeSchemaFormItem) {
  if (!isShown(item)) {
    return null
  }

  const scope = createScope(item)
  const prop = scope.prop
  const customItemSlot = prop ? resolveDynamicSlot(`item-${prop}`) : undefined
  const labelSlot = prop ? resolveDynamicSlot(`label-${prop}`) : undefined
  const itemLabelSlot = resolveItemSlot(item, scope, 'label')
  const itemProps = resolveItemProps(item, scope)
  const itemMeta = itemProps as Record<string, any>
  const itemHelp = itemMeta.help
  const itemExtra = itemMeta.extra
  const defaultItemSlot = item.itemSlots?.default
  const helpSlot = resolveItemSlot(item, scope, 'help')
  const extraSlot = resolveItemSlot(item, scope, 'extra')
  const labelTooltipContent = resolveLabelTooltipContent(item, scope)
  const resolvedLabel = resolveLabel(item)
  const labelTooltipProps = item.labelTooltipProps ?? {}

  const autoLabelContent = () => {
    if (!labelTooltipContent) {
      return resolvedLabel
    }

    return h('div', { class: 'inline-flex items-center gap-1.5' }, [
      h('span', null, resolvedLabel),
      h(FaTooltip as any, {
        text: typeof labelTooltipContent === 'string' ? labelTooltipContent : undefined,
        delay: labelTooltipProps.delay,
        side: labelTooltipProps.side,
        align: labelTooltipProps.align,
        disabled: labelTooltipProps.disabled,
      }, {
        default: () => h(FaIcon as any, {
          name: labelTooltipProps.icon ?? 'i-ri:question-line',
          class: labelTooltipProps.iconClass ?? 'cursor-help text-muted-foreground',
        }),
        content: typeof labelTooltipContent === 'string'
          ? undefined
          : () => labelTooltipContent,
      }),
    ])
  }

  const itemNode = h(
    ElFormItem,
    {
      label: labelSlot || itemLabelSlot || labelTooltipContent ? undefined : resolvedLabel,
      prop,
      ...itemProps,
      ref: prop ? (instance: any) => setFormItemRef(prop, instance) : undefined,
    },
    {
      ...(labelSlot
        ? { label: () => labelSlot(scope) }
        : itemLabelSlot
          ? { label: itemLabelSlot }
          : labelTooltipContent
            ? { label: autoLabelContent }
            : {}),
      error: resolveItemSlot(item, scope, 'error'),
      default: () => {
        if (item.renderColContent) {
          return item.renderColContent(scope)
        }

        if (customItemSlot) {
          return customItemSlot(scope)
        }

        const content = [
          resolveItemSlot(item, scope, 'prepend')?.(),
          defaultItemSlot?.(scope) ?? renderFieldContent(item),
          resolveItemSlot(item, scope, 'append')?.(),
        ]

        if (helpSlot || itemHelp) {
          content.push(h('div', { class: 'text-xs leading-5 text-[var(--el-text-color-secondary)]' }, helpSlot?.(scope) ?? itemHelp))
        }
        if (extraSlot || itemExtra) {
          content.push(h('div', { class: 'text-xs leading-5 text-[var(--el-text-color-secondary)]' }, extraSlot?.(scope) ?? itemExtra))
        }
        if (item.children?.length) {
          content.push(renderChildren(item.children))
        }

        return content
      },
    },
  )

  const hiddenStyle = isHiddenByHide(item) ? { display: 'none' } : undefined

  if (resolvedOptions.value.layout === 'grid') {
    return h('div', { key: prop ?? resolveLabel(item), style: hiddenStyle }, [itemNode])
  }

  return h(
    ElCol,
    {
      ...resolveColProps(item),
      key: prop ?? resolveLabel(item),
      style: hiddenStyle,
    },
    () => itemNode,
  )
}

function setItems(items: RuntimeSchemaFormItem[]) {
  innerItems.value = normalizeItems(items)
  void loadItemOptions()
}

function getItems() {
  return innerItems.value
}

function appendItem(item: RuntimeSchemaFormItem, index?: number) {
  const normalizedItem = normalizeItem(item)
  if (index == null || index < 0 || index >= innerItems.value.length) {
    innerItems.value.push(normalizedItem)
    void loadItemOptions()
    return
  }
  innerItems.value.splice(index, 0, normalizedItem)
  void loadItemOptions()
}

function prependItem(item: RuntimeSchemaFormItem) {
  innerItems.value.unshift(normalizeItem(item))
  void loadItemOptions()
}

function removeItem(prop: string) {
  const removed = removeItemRecursive(innerItems.value, prop)
  if (removed) {
    void loadItemOptions()
  }
  return removed
}

function updateItem(prop: string, updates: Partial<RuntimeSchemaFormItem>) {
  const updated = updateItemRecursive(innerItems.value, prop, normalizeItem(updates as RuntimeSchemaFormItem))
  if (updated) {
    void loadItemOptions()
  }
  return updated
}

function getItemByProp(prop: string) {
  return findItemByProp(innerItems.value, prop)
}

function getItemsByCondition(condition: (item: RuntimeSchemaFormItem) => boolean) {
  const items = flattenItems(innerItems.value) as RuntimeSchemaFormItem[]
  return items.filter(condition) as RuntimeSchemaFormItem[]
}

function getVisibleItems() {
  const items = flattenItems(innerItems.value) as RuntimeSchemaFormItem[]
  return items.filter(item => isShown(item) && !isHiddenByHide(item)) as RuntimeSchemaFormItem[]
}

function setOptions(options: Partial<SchemaFormOptions>) {
  innerOptions.value = { ...innerOptions.value, ...options }
}

function getOptions() {
  return innerOptions.value
}

function updateOptions(updater: (options: SchemaFormOptions) => SchemaFormOptions) {
  innerOptions.value = updater(innerOptions.value)
}

function setLoadingState(loading: boolean) {
  innerOptions.value = { ...innerOptions.value, loading }
}

function getLoadingState() {
  return Boolean(innerOptions.value.loading)
}

function isMobileState() {
  return isMobile.value
}

async function validate() {
  const valid = await formRef.value?.validate().catch(() => false)
  return Boolean(valid)
}

async function validateField(prop: string) {
  try {
    await formRef.value?.validateField(prop)
    return true
  }
  catch {
    return false
  }
}

function reset() {
  formRef.value?.resetFields()
}

function resetFields(props?: string[]) {
  formRef.value?.resetFields(props as any)
}

function clearValidate(props?: string[]) {
  formRef.value?.clearValidate(props as any)
}

function getValues() {
  return model.value as any
}

function getFormData() {
  return model.value as any
}

function syncInitialSnapshot() {
  initialModelSnapshot.value = structuredClone(toRaw(model.value))
}

function setValues(values: Partial<Record<string, any>>) {
  Object.assign(model.value, values)
}

function setFormData(values: Partial<Record<string, any>>) {
  Object.assign(model.value, values)
}

function resetFormData() {
  Object.keys(model.value).forEach((key) => {
    delete model.value[key]
  })
  Object.assign(model.value, structuredClone(initialModelSnapshot.value))
  formRef.value?.clearValidate()
}

function getElFormRef() {
  return formRef.value
}

function getComponentRef(prop: string) {
  return componentRefMap[prop]
}

function getFormItemRef(prop: string) {
  return formItemRefMap[prop]
}

function getComponentRefs() {
  return componentRefMap
}

function getFormItemRefs() {
  return formItemRefMap
}

async function loadItemOptions(changedKeys?: string[]) {
  const nextOptionMap: Record<string, SchemaFormOption[]> = { ...optionMap.value }
  await Promise.all(
    flattenItems(innerItems.value).map(async (item) => {
      const key = resolveItemProp(item)
      if (!key) {
        return
      }

      const scope = createScope(item)
      const dependsOnChangedKey = Boolean(item.dependencies?.some(dep => changedKeys?.includes(dep)))
      if (changedKeys?.length && item.dependencies?.length && !dependsOnChangedKey && optionMap.value[key]) {
        return
      }
      if (item.dictName) {
        const dictList = await dictionaryStore.get(item.dictName).catch(() => [])
        const valueType = inferDictValueType(item)
        const dictOptions = dictList.map((option: any) => ({
          label: option.name,
          value: normalizeDictValue(option.value, valueType),
          disabled: false,
        }))
        nextOptionMap[key] = dictOptions.length ? dictOptions : resolveDeclaredOptions(item, scope)
        return
      }
      if (item.optionRequest) {
        nextOptionMap[key] = await item.optionRequest(scope).catch(() => [])
        return
      }
      if (item.options) {
        nextOptionMap[key] = resolveDeclaredOptions(item, scope)
      }
    }),
  )
  optionMap.value = nextOptionMap
}

function getTrackedModelValues(items: RuntimeSchemaFormItem[]) {
  return flattenItems(items).map((item) => {
    const prop = resolveItemProp(item) ?? ''
    return {
      prop,
      value: getValueByPath(model.value, prop),
    }
  })
}

function getChangedTrackedKeys(
  current: Array<{ prop: string, value: any }>,
  previous?: Array<{ prop: string, value: any }>,
) {
  if (!previous) {
    return current.map(item => item.prop).filter(Boolean)
  }

  const previousMap = new Map(previous.map(item => [item.prop, item.value]))
  return current
    .filter(item => item.prop && previousMap.get(item.prop) !== item.value)
    .map(item => item.prop)
}

watch(
  innerItems,
  () => {
    void loadItemOptions()
  },
  { immediate: true },
)

watch(
  () => getTrackedModelValues(innerItems.value),
  (current, previous) => {
    const changedKeys = getChangedTrackedKeys(current, previous)
    const flattenedItems = flattenItems(innerItems.value)
    const affectedItems = flattenedItems.filter(item => item.dependencies?.some(dep => changedKeys.includes(dep)))
    if (affectedItems.length || flattenedItems.some(item => item.optionRequest || item.dictName)) {
      void loadItemOptions(changedKeys)
    }
    if (affectedItems.length) {
      nextTick(() => {
        formRef.value?.clearValidate(affectedItems.map(item => resolveItemProp(item)).filter(Boolean) as any)
      })
    }
  },
)

/**
 * 暴露给父组件的表单实例方法。
 * 方法签名以 `SchemaFormExpose` 为准，这里仅做运行时落地。
 */
const exposeApi: SchemaFormExpose = {
  setLoadingState,
  getLoadingState,
  setItems,
  getItems,
  appendItem,
  prependItem,
  removeItem,
  updateItem,
  getItemByProp,
  getItemsByCondition,
  getVisibleItems,
  setOptions,
  getOptions,
  updateOptions,
  isMobileState,
  validate,
  validateField,
  reset,
  resetFields,
  clearValidate,
  getValues,
  getFormData,
  setValues,
  setFormData,
  resetFormData,
  syncInitialSnapshot,
  getElFormRef,
  getComponentRef,
  getFormItemRef,
  getComponentRefs,
  getFormItemRefs,
}

defineExpose(exposeApi)
</script>

<template>
  <div
    v-loading="resolvedOptions.loading"
    class="mineadmin-form"
    :class="resolvedOptions.containerClass"
    :element-loading-text="resolvedOptions.loadingConfig?.text"
    :element-loading-spinner="resolvedOptions.loadingConfig?.spinner"
    :element-loading-svg="resolvedOptions.loadingConfig?.svg"
    :element-loading-svg-view-box="resolvedOptions.loadingConfig?.viewBox"
    :element-loading-background="resolvedOptions.loadingConfig?.background"
    :element-loading-custom-class="resolvedOptions.loadingConfig?.customClass"
    :element-loading-lock="resolvedOptions.loadingConfig?.lock"
    :element-loading-fullscreen="resolvedOptions.loadingConfig?.fullscreen"
  >
    <template v-if="$slots.loading && resolvedOptions.loading">
      <slot name="loading" :loading="resolvedOptions.loading" />
    </template>

    <ElForm ref="formRef" v-bind="formBindProps">
      <div v-if="resolvedOptions.beforeSlot" class="mb-5">
        <component :is="{ render: () => resolvedOptions.beforeSlot?.(createFooterScope()) }" />
      </div>
      <div v-if="$slots.before" class="mb-5">
        <slot name="before" />
      </div>

      <slot v-if="hasDefaultSlot" />

      <ElRow
        v-else-if="resolvedOptions.layout === 'flex'"
        :gutter="resolvedOptions.flex?.gutter"
        :justify="resolvedOptions.flex?.justify"
        :align="resolvedOptions.flex?.align"
        :tag="resolvedOptions.flex?.tag"
      >
        <template v-for="item in innerItems" :key="resolveItemProp(item) ?? resolveLabel(item)">
          <component :is="{ render: () => renderSingleItem(item) }" />
        </template>
      </ElRow>

      <ElSpace
        v-else
        :direction="resolvedOptions.grid?.direction"
        :size="resolvedOptions.grid?.size"
        :wrap="resolvedOptions.grid?.wrap"
        :fill="resolvedOptions.grid?.fill"
        :fill-ratio="resolvedOptions.grid?.fillRatio"
        :alignment="resolvedOptions.grid?.alignment"
        :class="resolvedOptions.grid?.class"
        :prefix-cls="resolvedOptions.grid?.prefixCls"
        :style="resolvedOptions.grid?.style"
        :spacer="resolvedOptions.grid?.spacer"
      >
        <template v-for="item in innerItems" :key="resolveItemProp(item) ?? resolveLabel(item)">
          <component :is="{ render: () => renderSingleItem(item) }" />
        </template>
      </ElSpace>

      <div v-if="resolvedOptions.footerSlot" class="mt-6">
        <component :is="{ render: () => resolvedOptions.footerSlot?.(createFooterScope()) }" />
      </div>
      <div v-if="$slots.footer" class="mt-6">
        <slot name="footer" :form-ref="formRef" :model="model" :loading="Boolean(resolvedOptions.loading)" />
      </div>
      <div v-if="resolvedOptions.afterSlot" class="mt-4">
        <component :is="{ render: () => resolvedOptions.afterSlot?.(createFooterScope()) }" />
      </div>
    </ElForm>
  </div>
</template>
